import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import PropertyActivity from "@/modules/property-tracks/components/activity-tab";
import PropertyConstruction from "@/modules/property-tracks/components/construction-tab";
import PropertyDocuments from "@/modules/property-tracks/components/documents-tab";
import PropertyOverview from "@/modules/property-tracks/components/overview-tab";
import { Track } from "@/modules/property-tracks/types";
import { Box, Skeleton } from "@mui/material";
import SubPropertiesTab from "@/modules/property-tracks/components/sub-properties-tab";
import LinkedTracksTab from "@/modules/property-tracks/components/linked-tracks-tab";
import RentalActiveListingTab from "@/modules/property-tracks/components/listing-tab/rental-listing";
import HospitalityActiveListingTab from "@/modules/property-tracks/components/listing-tab/hospitality-listing";
import PageComponentsWrapper from "@/modules/common/components/wrappers/page-components-wrapper";
import InterestsTab from "@/modules/property-tracks/components/interests-tab";
import ResaleActiveListingDesktopTab from "@/modules/property-tracks/components/listing-tab/resale-listing";
import { usePropertyTrackTabs } from "@/modules/property-tracks/hooks/property-tracker-tab";
import TenantsTab from "@/modules/property-tracks/components/tenants-tab";
import DrawingProposalsTab from "@/modules/property-tracks/components/drawing-proposals-tab";

function TabContent({
  onError,
  track,
  isLoading,
}: {
  onError?: () => void;
  track?: Track;
  isLoading: boolean;
}) {
  const { selectedTab } = usePropertyTrackTabs({ track });
  const renderTabContent = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="100%" height="100%" />;
    }

    if (!track) {
      onError?.();
      return;
    }

    switch (selectedTab) {
      case "Activity":
        return <PropertyActivity track={track} />;
      case "Overview":
        return <PropertyOverview track={track} />;
      case "Documents":
        return <PropertyDocuments track={track} />;
      case "Construction":
        return <PropertyConstruction track={track} />;
      case "SubProperties":
        return (
          <SubPropertiesTab
            property={track.property}
            trackTypeName={track.trackType.name}
          />
        );
      case "LinkedTracks":
        return <LinkedTracksTab track={track} />;
      case "Listing":
        return (
          <>
            <PageComponentsWrapper
              trackTypeId={track.trackType.id}
              page="property-details"
              component="property-listing-tab"
            >
              <RentalActiveListingTab track={track} />
            </PageComponentsWrapper>
            <PageComponentsWrapper
              trackTypeId={track.trackType.id}
              page="property-details"
              component="property-resale-listing-tab"
            >
              <ResaleActiveListingDesktopTab track={track} />
            </PageComponentsWrapper>
            <PageComponentsWrapper
              trackTypeId={track.trackType.id}
              page="property-details"
              component="property-hospitality-listing-tab"
            >
              <HospitalityActiveListingTab track={track} />
            </PageComponentsWrapper>
          </>
        );
      case "Interests":
        return (
          <PageComponentsWrapper
            trackTypeId={track.trackType.id}
            page="property-details"
            component="property-interests-tab"
          >
            <InterestsTab propertyUid={track.property.uid} />
          </PageComponentsWrapper>
        );
      case "Tenants":
        return (
          <PageComponentsWrapper
            trackTypeId={track.trackType.id}
            page="property-details"
            component="property-tenants-tab"
          >
            <TenantsTab propertyUid={track.property.uid} />
          </PageComponentsWrapper>
        );
      case "DrawingProposals":
        return (
          <PageComponentsWrapper
            trackTypeId={track.trackType.id}
            page="property-details"
            component="drawing-propsal-requests"
          >
            <DrawingProposalsTab
              propertyUid={track.property.uid}
              customerUid={track.owner?.uid ?? ""}
              isContractSigned={
                track.status.name.toLowerCase() === "contract signed"
              }
            />
          </PageComponentsWrapper>
        );
      default:
        if (onError) {
          onError();
        }
        return <Box sx={{ padding: 2 }}>Tab content not found.</Box>;
    }
  };

  return <>{renderTabContent()}</>;
}

const TabContentWithError = WithErrorHandling(TabContent);

export default TabContentWithError;
